/**
 * Rail model: turns the (already permission-filtered) nav config into the
 * two-level navigation the shell renders, Accelimate-style (PM efb0e798):
 *
 *   rail      one icon + short label per section (Members, Billing, …)
 *   page list the chosen section's pages, in a second column
 *
 * Pure data, no state. The filtering itself (section permissions, adminOnly,
 * module gates, page permissions) stays in SideNavbarLayout and runs BEFORE
 * this, so nothing here can surface a page the person's role can't open.
 */
import React from "react";
import {
  Building2,
  CalendarDays,
  GraduationCap,
  Home,
  LifeBuoy,
  Receipt,
  ShoppingBag,
  Sun,
  User as UserIcon,
  Users,
} from "lucide-react";
import type { MemberMenuSection, NavGroup, NavItem, NavSectionConfig } from "./navConfig";

export interface RailPage {
  to: string;
  label: string;
  comingSoon?: boolean;
}

/** A run of pages in the page list; `label` is a sub-heading (a nested group
 *  such as "Broadcasts", or a category). null renders no heading. */
export interface RailPageGroup {
  label: string | null;
  pages: RailPage[];
}

export interface RailSection {
  key: string;
  title: string;
  /** shorter label for the narrow rail; the full title is the accessible name */
  shortTitle?: string;
  icon: React.ReactNode;
  /** muted group label (People, Commerce, …); members have none */
  groupLabel: string | null;
  /** settings-type sections sit at the bottom of the rail */
  placement: "main" | "bottom";
  /** where the rail button goes when the section has a single page */
  href: string;
  pageGroups: RailPageGroup[];
  /** hard-gated module the club hasn't bought: shown, padlocked, inert */
  locked?: boolean;
}

/** Sections pinned to the bottom of the staff rail. */
const STAFF_BOTTOM_KEYS = new Set(["settings"]);

/** Short rail labels where the section title is too long for the 80px rail. */
const SHORT_TITLES: Record<string, string> = {};

const iconCls = "w-5 h-5 flex-shrink-0";

function pageGroupsFromItems(items: NavItem[]): RailPageGroup[] {
  const groups: RailPageGroup[] = [];
  let current: RailPageGroup = { label: null, pages: [] };
  const flush = () => {
    if (current.pages.length > 0) groups.push(current);
  };
  for (const item of items) {
    if ("isCategory" in item) {
      flush();
      current = { label: item.label, pages: [] };
    } else if ("isNested" in item) {
      flush();
      groups.push({
        label: item.label,
        pages: item.items.map((s) => ({ to: s.to, label: s.label, comingSoon: s.comingSoon })),
      });
      current = { label: null, pages: [] };
    } else {
      current.pages.push({ to: item.to, label: item.label, comingSoon: item.comingSoon });
    }
  }
  flush();
  return groups;
}

export const sectionPages = (section: RailSection): RailPage[] =>
  section.pageGroups.flatMap((g) => g.pages);

/** A section with one page (or none) has nothing to choose between: its
 *  rail button goes straight there and no page list shows. */
export const isSinglePage = (section: RailSection) => sectionPages(section).length < 2;

/** Staff: one rail section per (filtered) navConfig section. */
export function buildStaffRail(groups: NavGroup[]): RailSection[] {
  const out: RailSection[] = [];
  for (const group of groups) {
    for (const section of group.sections as NavSectionConfig[]) {
      const pageGroups = section.isDirectLink ? [] : pageGroupsFromItems(section.items);
      const pages = pageGroups.flatMap((g) => g.pages);
      out.push({
        key: section.sectionKey,
        title: section.title,
        shortTitle: SHORT_TITLES[section.sectionKey],
        icon: section.icon,
        groupLabel: group.label,
        placement: STAFF_BOTTOM_KEYS.has(section.sectionKey) ? "bottom" : "main",
        href: pages.length === 1 ? pages[0].to : section.directLink,
        pageGroups,
        locked: section.locked,
      });
    }
  }
  return out;
}

interface MemberRailInput {
  tenantId: string | null;
  t: (key: string, opts?: any) => any;
  sections: MemberMenuSection[];
  generalLinks: Array<{ to: string; label: string; hidden?: boolean }>;
}

const MEMBER_SECTION_ICONS: Record<string, React.ReactNode> = {
  member_events: <CalendarDays className={iconCls} />,
  member_camps: <Sun className={iconCls} />,
  member_clinics: <GraduationCap className={iconCls} />,
};

/**
 * Families: the member menu (buildMemberMenus) as rail sections. Events,
 * Camps and Clinics keep their pages; the single links become single-page
 * sections; the two facility links share a section; Help, Feedback and
 * Support share the bottom Help section. Hidden links stay hidden.
 */
export function buildMemberRail(input: MemberRailInput): RailSection[] {
  const { tenantId, t, sections, generalLinks } = input;
  const visible = generalLinks.filter((l) => !l.hidden);
  const link = (suffix: string) => visible.find((l) => l.to === `/${tenantId}/member/${suffix}`);
  const single = (
    key: string,
    page: { to: string; label: string } | undefined,
    icon: React.ReactNode,
    placement: "main" | "bottom" = "main"
  ): RailSection | null =>
    page
      ? {
          key,
          title: page.label,
          icon,
          groupLabel: null,
          placement,
          href: page.to,
          pageGroups: [{ label: null, pages: [{ to: page.to, label: page.label }] }],
        }
      : null;

  const home: RailSection = {
    key: "member_home",
    title: t("nav.home", { defaultValue: "Home" }),
    icon: <Home className={iconCls} />,
    groupLabel: null,
    placement: "main",
    href: `/${tenantId}/dashboard`,
    pageGroups: [],
  };

  const menuSections: RailSection[] = sections.map((s) => ({
    key: s.navKey,
    title: s.title,
    icon: MEMBER_SECTION_ICONS[s.navKey] ?? <CalendarDays className={iconCls} />,
    groupLabel: null,
    placement: "main",
    href: s.items[0]?.to || "#",
    pageGroups: [{ label: null, pages: s.items.map((i) => ({ to: i.to, label: i.label })) }],
  }));

  const facilityPages = [link("facilities"), link("facilities/bookings")].filter(
    (p): p is { to: string; label: string } => !!p
  );
  const facilities: RailSection | null =
    facilityPages.length > 0
      ? {
          key: "member_facilities",
          title: t("nav.facilities", { defaultValue: "Facilities" }),
          icon: <Building2 className={iconCls} />,
          groupLabel: null,
          placement: "main",
          href: facilityPages[0].to,
          pageGroups: [{ label: null, pages: facilityPages }],
        }
      : null;

  const helpPages = [link("help"), link("feedback"), link("contact")].filter(
    (p): p is { to: string; label: string } => !!p
  );
  const help: RailSection | null =
    helpPages.length > 0
      ? {
          key: "member_help",
          title: t("nav.help", { defaultValue: "Help" }),
          icon: <LifeBuoy className={iconCls} />,
          groupLabel: null,
          placement: "bottom",
          href: helpPages[0].to,
          pageGroups: [{ label: null, pages: helpPages }],
        }
      : null;

  return [
    home,
    ...menuSections,
    single("member_billing", link("view-billing"), <Receipt className={iconCls} />),
    single("member_athletes", link("manage-athlete"), <Users className={iconCls} />),
    facilities,
    single("member_store", link("store"), <ShoppingBag className={iconCls} />),
    single("member_profile", link("manage-profile"), <UserIcon className={iconCls} />),
    help,
  ].filter((s): s is RailSection => !!s);
}

/**
 * The page the current route belongs to: an exact match, else the deepest
 * page whose path the route sits under (/manage/memberships/123 belongs to
 * Manage Memberships). Locked sections never hold the route.
 */
export function findActive(
  sections: RailSection[],
  pathname: string
): { sectionKey: string | null; href: string | null } {
  let best: { sectionKey: string; href: string } | null = null;
  for (const section of sections) {
    if (section.locked) continue;
    const candidates = [section.href, ...sectionPages(section).map((p) => p.to)];
    for (const to of candidates) {
      if (!to || to === "#") continue;
      if (pathname === to) return { sectionKey: section.key, href: to };
      if (pathname.startsWith(to + "/") && (!best || to.length > best.href.length)) {
        best = { sectionKey: section.key, href: to };
      }
    }
  }
  return best ? best : { sectionKey: null, href: null };
}
